import { captureException, captureMessage } from "@sentry/browser";
import { isHttpError } from "~/utils/httpClient";
import { SimulatedSessionFailure } from "./sessionTestControls";
import { getSessionErrorDiagnostics } from "./sessionErrorDiagnostics";

export type SessionInitializationStage =
  | "authentication"
  | "session"
  | "user"
  | "business-list"
  | "business";

export type SessionInitializationFailure = {
  stage: SessionInitializationStage;
  kind: "authentication" | "api" | "network" | "unknown";
  status?: number;
  simulated?: boolean;
  diagnostics?: ReturnType<typeof getSessionErrorDiagnostics>;
};

export const getSessionInitializationFailure = (
  error: unknown,
  stage: SessionInitializationStage,
): SessionInitializationFailure => {
  if (error instanceof SimulatedSessionFailure) {
    return {
      stage: error.stage,
      kind: error.stage === "authentication" ? "authentication" : "network",
      simulated: true,
    };
  }
  const diagnostics = getSessionErrorDiagnostics(error);
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string" &&
    error.code.startsWith("auth/")
  ) {
    return { stage, kind: "authentication", diagnostics };
  }
  if (isHttpError(error)) {
    const status = error.response?.status;
    return {
      stage,
      kind: status ? "api" : "network",
      ...(status ? { status } : {}),
      diagnostics,
    };
  }
  return {
    stage,
    kind: stage === "authentication" ? "authentication" : "unknown",
    diagnostics,
  };
};

export const reportSessionInitializationFailure = (
  failure: SessionInitializationFailure,
) => {
  if (failure.simulated) return;
  const {
    sourceStack,
    ...diagnostics
  }: Partial<ReturnType<typeof getSessionErrorDiagnostics>> =
    failure.diagnostics ?? {};
  const context = {
    level: "warning" as const,
    tags: {
      stage: failure.stage,
      kind: failure.kind,
      "session.reason": diagnostics.reason,
      "session.error_type": diagnostics.errorType,
      "session.error_code": diagnostics.code,
      "session.diagnostics_version": "1",
      "session.endpoint": diagnostics.response?.endpoint,
      "session.http_status": diagnostics.response?.status,
      "session.content_type": diagnostics.response?.contentType,
    },
    extra: { status: failure.status, diagnostics },
    fingerprint: ["session-initialization", failure.stage, failure.kind],
  };
  if (sourceStack) {
    const sanitizedError = new Error("Session initialization failed");
    sanitizedError.stack = sourceStack;
    captureException(sanitizedError, context);
  } else {
    captureMessage("Session initialization failed", context);
  }
};
